/**
 * "Build with AI" for subtask templates (2026-10-31, branch claude/template-builder).
 * The server (`POST api/ai/template-draft`) returns a draft; the window edits it here
 * and saves it with the EXISTING `POST api/work-template/add-template` (one call,
 * subtasks included, in the order shown). Nothing is saved before Save.
 */
import { DUE_DAYS_MAX, DUE_DAYS_MIN } from "../../../../../utils/dueDays";

export type DraftMode = "describe" | "paste" | "past_work";
export type DueRule = "none" | "after_start" | "before_due";

/** As the server sends it (camelCase of AiTemplateDraftTask). */
export type ServerDraftTask = {
  title: string;
  details?: string | null;
  noOfDays?: number | null;
  isAfterStartDate?: boolean | null;
  hours?: number | null;
  assignedUserId?: string | null;
};

export type ServerDraft = {
  name: string;
  description?: string | null;
  tasks: ServerDraftTask[];
  warnings?: string[];
};

export type DraftTeamMember = { id: string; name: string; roles: string[] };
export type DraftWorkType = { id: string; name: string; pastWorkItems: number };

/** One row in the window. `key` only keeps React rows stable. */
export type DraftRow = {
  key: string;
  title: string;
  details: string;
  dueRule: DueRule;
  /** Text box value; used only when dueRule isn't "none". */
  days: string;
  /** Text box value; "" = no hours. */
  hours: string;
  assignedUserId: string;
};

export type EditableDraft = {
  name: string;
  description: string;
  rows: DraftRow[];
};

export const MAX_ROWS = 40;
export const MAX_TITLE = 100;
export const MAX_NAME = 100;
export const MAX_HOURS = 100;

export const EXAMPLES = [
  "T1 personal return",
  "T2 year-end",
  "New client onboarding",
  "Monthly bookkeeping",
];

let keySeq = 0;
const nextKey = () => `row-${Date.now().toString(36)}-${++keySeq}`;

export const emptyRow = (): DraftRow => ({
  key: nextKey(),
  title: "",
  details: "",
  dueRule: "none",
  days: "",
  hours: "",
  assignedUserId: "",
});

/** Server draft -> rows of the window. */
export const fromServerDraft = (draft: ServerDraft): EditableDraft => ({
  name: draft.name || "",
  description: draft.description || "",
  rows: (draft.tasks || []).slice(0, MAX_ROWS).map((t) => {
    const hasDays = t.noOfDays !== null && t.noOfDays !== undefined;
    return {
      key: nextKey(),
      title: t.title || "",
      details: t.details || "",
      dueRule: !hasDays
        ? "none"
        : t.isAfterStartDate === false
        ? "before_due"
        : "after_start",
      days: hasDays ? String(t.noOfDays) : "",
      hours: t.hours && t.hours > 0 ? String(t.hours) : "",
      assignedUserId: t.assignedUserId || "",
    };
  }),
});

const parseWhole = (text: string, min: number, max: number) => {
  const v = text.trim();
  if (!/^\d+$/.test(v)) return null;
  const n = Number(v);
  return n >= min && n <= max ? n : null;
};

/** The row's due days, or null for "none" / an invalid number. */
export const rowDays = (row: DraftRow): number | null =>
  row.dueRule === "none"
    ? null
    : parseWhole(row.days, DUE_DAYS_MIN, DUE_DAYS_MAX);

export const rowHours = (row: DraftRow): number | null => {
  const n = parseWhole(row.hours, 0, MAX_HOURS);
  return n && n > 0 ? n : null;
};

/** "3 days after start" / "2 days before due" / "On the work's due date". */
export const dueRuleText = (rule: DueRule, days: number | null): string => {
  if (rule === "none" || days === null) return "On the work's due date";
  const unit = days === 1 ? "day" : "days";
  return rule === "after_start"
    ? days === 0
      ? "On the start date"
      : `${days} ${unit} after start`
    : days === 0
    ? "On the work's due date"
    : `${days} ${unit} before due`;
};

export const totalHours = (rows: DraftRow[]) =>
  rows.reduce((sum, r) => sum + (rowHours(r) ?? 0), 0);

/** "8 subtasks · ~12 h" (hours only when some row has them). */
export const draftSummary = (rows: DraftRow[]) => {
  const count = rows.filter((r) => r.title.trim()).length;
  const hours = totalHours(rows);
  const tasks = `${count} subtask${count === 1 ? "" : "s"}`;
  return hours > 0 ? `${tasks} · ~${hours} h` : tasks;
};

export const moveRow = (rows: DraftRow[], from: number, to: number) => {
  if (from === to || from < 0 || from >= rows.length) return rows;
  const target = Math.max(0, Math.min(rows.length - 1, to));
  const next = rows.slice();
  const [item] = next.splice(from, 1);
  next.splice(target, 0, item);
  return next;
};

export const updateRow = (
  rows: DraftRow[],
  key: string,
  patch: Partial<DraftRow>,
) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r));

export const removeRow = (rows: DraftRow[], key: string) =>
  rows.filter((r) => r.key !== key);

export const addRow = (rows: DraftRow[]) =>
  rows.length >= MAX_ROWS ? rows : [...rows, emptyRow()];

/** What stops Save, in plain words (null = can save). Same limits as the server. */
export const draftProblem = (draft: EditableDraft): string | null => {
  const name = draft.name.trim();
  if (!name) return "Enter a template name.";
  if (name.length > MAX_NAME)
    return `Template names can be at most ${MAX_NAME} characters.`;
  const rows = draft.rows.filter((r) => r.title.trim());
  if (rows.length === 0) return "Add at least one subtask.";
  if (rows.length > MAX_ROWS)
    return `A template can have at most ${MAX_ROWS} subtasks here.`;
  const long = rows.find((r) => r.title.trim().length > MAX_TITLE);
  if (long) return `Subtask titles can be at most ${MAX_TITLE} characters.`;
  if (rows.some((r) => r.dueRule !== "none" && rowDays(r) === null))
    return "Use a whole number of days from 0 to 365.";
  if (
    rows.some(
      (r) => r.hours.trim() && parseWhole(r.hours, 0, MAX_HOURS) === null,
    )
  )
    return `Hours must be a whole number from 0 to ${MAX_HOURS}.`;
  return null;
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Plain details -> the editor's HTML (paragraphs; the subtask editor stores HTML). */
export const detailsToHtml = (text: string) => {
  const t = text.trim();
  if (!t) return "";
  return t
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p.trim()).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

/** Body for the existing `api/work-template/add-template` (subtasks in the order shown). */
export const toAddTemplatePayload = (draft: EditableDraft, orgId: string) => ({
  templateName: draft.name.trim(),
  description: draft.description.trim(),
  isActive: true,
  orgId: null,
  orgRelayId: orgId,
  workTemplateTasks: draft.rows
    .filter((r) => r.title.trim())
    .map((r, index) => {
      const noOfDays = rowDays(r);
      return {
        name: r.title.trim(),
        description: detailsToHtml(r.details),
        timeBudget: rowHours(r) ?? 0,
        assignedUserId: r.assignedUserId || null,
        order: index + 1,
        isDone: false,
        noOfDays,
        isAfterStartDate:
          noOfDays === null ? true : r.dueRule === "after_start",
      };
    }),
});

/** The edited draft sent back with "Ask for changes" (server shape). */
export const toServerDraft = (draft: EditableDraft): ServerDraft => ({
  name: draft.name.trim(),
  description: draft.description.trim() || null,
  tasks: draft.rows
    .filter((r) => r.title.trim())
    .map((r) => {
      const noOfDays = rowDays(r);
      return {
        title: r.title.trim(),
        details: r.details.trim() || null,
        noOfDays,
        isAfterStartDate:
          noOfDays === null ? null : r.dueRule === "after_start",
        hours: rowHours(r),
        assignedUserId: r.assignedUserId || null,
      };
    }),
});

/** "Jane Doe (Bookkeeper, Reviewer)" for the assignee list. */
export const memberLabel = (m: DraftTeamMember) =>
  m.roles.length ? `${m.name} (${m.roles.join(", ")})` : m.name;
